import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows } from "@/lib/adminApi";
import type { Payment } from "@/lib/adminCommerce";
import {
  DataTable,
  ErrorBanner,
  LoadingState,
  PageHeader,
  StatCards,
  btnGhost,
  btnPrimary,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatRupees } from "@/pages/admin/format";
import { exportToCsv, exportToPdf } from "@/pages/admin/exportUtils";
import { aggregatePaymentsByMonth, type MonthRevenue } from "@/lib/adminReports";

interface FarmRevenue {
  id: number;
  farm: string;
  collected: number;
  transactions: number;
}

export default function AdminRevenueReports() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setPayments(await listRows<Payment>("payments", "paid_at"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load payments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const monthly = useMemo(() => aggregatePaymentsByMonth(payments, 12), [payments]);

  const totals = useMemo(
    () =>
      payments.reduce(
        (acc, payment) => {
          const amount = Number(payment.amount || 0);
          if (payment.status === "paid") acc.collected += amount;
          else if (payment.status === "pending") acc.pending += amount;
          else if (payment.status === "refunded") acc.refunded += amount;
          return acc;
        },
        { collected: 0, pending: 0, refunded: 0 },
      ),
    [payments],
  );

  const farmRevenue = useMemo<FarmRevenue[]>(() => {
    const map = new Map<string, FarmRevenue>();
    payments
      .filter((payment) => payment.status === "paid")
      .forEach((payment) => {
        const farm = payment.farm_name || "Unassigned";
        const existing = map.get(farm) || { id: 0, farm, collected: 0, transactions: 0 };
        existing.collected += Number(payment.amount || 0);
        existing.transactions += 1;
        map.set(farm, existing);
      });
    return Array.from(map.values())
      .sort((a, b) => b.collected - a.collected)
      .map((row, index) => ({ ...row, id: index + 1 }));
  }, [payments]);

  const monthColumns: Column<MonthRevenue>[] = [
    { key: "month", header: "Month", primary: true, render: (row) => <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.label}</span> },
    { key: "collected", header: "Collected", render: (row) => <span className="text-sm text-foreground-800 whitespace-nowrap">{formatRupees(row.collected)}</span> },
    { key: "pending", header: "Pending", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{formatRupees(row.pending)}</span> },
    { key: "refunded", header: "Refunded", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{formatRupees(row.refunded)}</span> },
    { key: "net", header: "Net", render: (row) => <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatRupees(row.net)}</span> },
    { key: "txns", header: "Txns", render: (row) => <span className="text-sm text-foreground-700">{row.transactions}</span> },
  ];

  const farmColumns: Column<FarmRevenue>[] = [
    { key: "farm", header: "Farm", primary: true, render: (row) => <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.farm}</span> },
    { key: "collected", header: "Collected", render: (row) => <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatRupees(row.collected)}</span> },
    { key: "txns", header: "Transactions", render: (row) => <span className="text-sm text-foreground-700">{row.transactions}</span> },
  ];

  const net = totals.collected - totals.refunded;

  const monthExport = [
    { header: "Month", value: (row: MonthRevenue) => row.label },
    { header: "Collected", value: (row: MonthRevenue) => `${row.collected}` },
    { header: "Pending", value: (row: MonthRevenue) => `${row.pending}` },
    { header: "Refunded", value: (row: MonthRevenue) => `${row.refunded}` },
    { header: "Net", value: (row: MonthRevenue) => `${row.net}` },
    { header: "Transactions", value: (row: MonthRevenue) => `${row.transactions}` },
  ];
  const farmExport = [
    { header: "Farm", value: (row: FarmRevenue) => row.farm },
    { header: "Collected", value: (row: FarmRevenue) => `${row.collected}` },
    { header: "Transactions", value: (row: FarmRevenue) => `${row.transactions}` },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Reports"
        title="Revenue Reports"
        subtitle="How money moves — collected, pending and refunded by month, plus the farm-by-farm breakdown."
        actions={
          <>
            <button type="button" className={btnGhost} onClick={() => exportToCsv("rythugaru-revenue-monthly", monthExport, monthly)}>
              <i className="ri-file-excel-2-line"></i> CSV
            </button>
            <button type="button" className={btnPrimary} onClick={() => exportToPdf({ title: "Revenue Report", subtitle: "Monthly collection summary", columns: monthExport, rows: monthly })}>
              <i className="ri-file-pdf-2-line"></i> PDF
            </button>
          </>
        }
      />

      <StatCards
        stats={[
          { label: "Collected", value: formatRupees(totals.collected), icon: "ri-money-rupee-circle-line", tone: "primary" },
          { label: "Pending", value: formatRupees(totals.pending), icon: "ri-time-line", tone: "accent" },
          { label: "Refunded", value: formatRupees(totals.refunded), icon: "ri-refund-2-line", tone: "secondary" },
          { label: "Net revenue", value: formatRupees(net), icon: "ri-line-chart-line", tone: "primary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      {loading ? (
        <div className="mt-5">
          <LoadingState label="Loading revenue…" />
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          <section>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="font-heading text-xl text-foreground-950 font-semibold">Last 12 months</h2>
              <button type="button" className={btnGhost} onClick={() => exportToCsv("rythugaru-revenue-by-farm", farmExport, farmRevenue)}>
                <i className="ri-download-2-line"></i> Farm CSV
              </button>
            </div>
            <DataTable columns={monthColumns} rows={monthly} />
          </section>

          <section>
            <h2 className="font-heading text-xl text-foreground-950 font-semibold mb-3">Revenue by farm</h2>
            <DataTable columns={farmColumns} rows={farmRevenue} />
          </section>
        </div>
      )}
    </div>
  );
}